import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'

import { BuilderNavbarSettings } from '@/adapters/__project__/BuilderNavbarAdapter'

import { MdShape } from './builder/types'

export type NavSettingOptions = 'select option' | 'menu' | 'logo' | 'elements'

export interface NavDisplayOption {
  label: string
  name: string
  checked: boolean
  setting?: NavSettingOptions
}

const createDisplayOption = (label: string, name: string, checked: boolean = true): NavDisplayOption => ({
  label,
  name,
  checked,
})

export const DEFAULT_NAV_DISPLAY_OPTIONS = [
  createDisplayOption('Sticky', 'sticky'),
  createDisplayOption('Show Logo', 'show_logo'),
  createDisplayOption('Show Icon', 'show_icon'),
  createDisplayOption('Show Label Text', 'show_label'),
  createDisplayOption('Show Hover', 'show_hover'),
  createDisplayOption('Show Shadow', 'show_shadow'),
  // createDisplayOption('Show Badge', 'show_badge'),
  createDisplayOption('Show Active Indicator', 'show_active_indicator'),
]

export const DEFAULT_NAV_SETTINGS: BuilderNavbarSettings = {
  ['phone']: {
    title: 'Mobile (Portrait)',
    viewport: 'phone',
    position: 'bottom',
    spacing: 'close',
    iconDirection: 'vertical',
    menuAlignment: 'center',
    logoAlignment: 'leading',
    menuHeight: '85px',
    menuWidth: '100%',
    logoWidth: '50px',
    logo: { value: '', label: '' },
    logoDark: { value: '', label: '' },
    menuBgColor: 'primary-20',
    menuBgColorDark: 'primary-90',
    menuTextColor: '',
    cornerRadius: MdShape.Small,
    displayOptions: DEFAULT_NAV_DISPLAY_OPTIONS,
    maxItems: 100,
    show: true,
  },
  'phone-landscape': {
    title: 'Mobile (Landscape)',
    viewport: 'phone-landscape',
    position: 'bottom',
    spacing: 'close',
    iconDirection: 'vertical',
    menuAlignment: 'leading',
    logoAlignment: 'leading',
    menuHeight: '80px',
    menuWidth: '100%',
    logoWidth: '50px',
    logo: { value: '', label: '' },
    logoDark: { value: '', label: '' },
    menuBgColor: 'primary-20',
    menuBgColorDark: 'primary-90',
    menuTextColor: '',
    cornerRadius: MdShape.Small,
    displayOptions: DEFAULT_NAV_DISPLAY_OPTIONS,
    maxItems: 100,
    show: true,
  },
  ['tablet']: {
    title: 'Tablet (Portrait)',
    viewport: 'tablet',
    position: 'bottom',
    spacing: 'close',
    iconDirection: 'vertical',
    menuAlignment: 'center',
    logoAlignment: 'leading',
    menuHeight: '80px',
    menuWidth: '100%',
    logoWidth: '50px',
    logo: { value: '', label: '' },
    logoDark: { value: '', label: '' },
    menuBgColor: 'primary-20',
    menuBgColorDark: 'primary-90',
    menuTextColor: '',
    cornerRadius: MdShape.Small,
    displayOptions: DEFAULT_NAV_DISPLAY_OPTIONS,
    maxItems: 100,
    show: true,
  },
  'tablet-landscape': {
    title: 'Tablet (Landscape)',
    viewport: 'tablet-landscape',
    position: 'leading',
    spacing: 'close',
    menuAlignment: 'leading',
    iconDirection: 'vertical',
    logoAlignment: 'leading',
    menuHeight: '70px',
    menuWidth: '120px',
    logoWidth: '120px',
    logo: { value: '', label: '' },
    logoDark: { value: '', label: '' },
    menuBgColor: 'primary-20',
    menuBgColorDark: 'primary-90',
    menuTextColor: '',
    cornerRadius: MdShape.Small,
    displayOptions: DEFAULT_NAV_DISPLAY_OPTIONS,
    maxItems: 100,
    show: true,
  },
  ['desktop']: {
    title: 'Laptop/Desktop',
    viewport: 'desktop',
    position: 'top',
    spacing: 'close',
    iconDirection: 'horizontal',
    menuAlignment: 'leading',
    logoAlignment: 'leading',
    menuHeight: '100px',
    menuWidth: '100%',
    logoWidth: '100px',
    logo: { value: '', label: '' },
    logoDark: { value: '', label: '' },
    menuBgColor: 'primary-20',
    menuBgColorDark: 'primary-90',
    menuTextColor: '',
    cornerRadius: MdShape.Small,
    displayOptions: DEFAULT_NAV_DISPLAY_OPTIONS,
    maxItems: 10,
    show: true,
  },
  ['tv']: {
    title: 'TV',
    viewport: 'tv',
    position: 'top',
    spacing: 'close',
    iconDirection: 'vertical',
    menuAlignment: 'leading',
    logoAlignment: 'leading',
    menuHeight: '110px',
    menuWidth: '100%',
    logoWidth: '110px',
    logo: { value: '', label: '' },
    logoDark: { value: '', label: '' },
    menuBgColor: 'primary-20',
    menuBgColorDark: 'primary-90',
    menuTextColor: '',
    cornerRadius: MdShape.Small,
    displayOptions: DEFAULT_NAV_DISPLAY_OPTIONS,
    maxItems: 10,
    show: true,
  },
}

export const SORTED_NAV_SETTINGS_KEY: AppBreakpoint[] = [
  'phone',
  'phone-landscape',
  'tablet',
  'tablet-landscape',
  'desktop',
  'tv',
]

export const removeLineHeight = (fontString: string) => {
  const fontRegex = /^(\d+|normal|bold|italic)\s([\d.]+rem|em|px|%|pt)\s*(?:\/\s*[\d.]+rem)?\s+"([^"]+)"$/

  const match = fontString.match(fontRegex)

  if (match) {
    const fontWeight = match[1]
    const fontSize = match[2]
    const fontFamily = match[3]

    return `${fontWeight} ${fontSize} "${fontFamily}"`
  }
  return fontString
}

export const viewportMaxWidthMap: Record<AppBreakpoint, number> = {
  desktop: 1920,
  phone: 480,
  'phone-landscape': 896,
  tablet: 768,
  'tablet-landscape': 1024,
  tv: 3840,
}

export interface NavbarConfigGroups {
  menu: string[]
  logo: string[]
  elements: string[]
}

export const navbarConfigGroups: NavbarConfigGroups = {
  menu: [
    'position',
    'menuBgColor',
    'iconDirection',
    'menuAlignment',
    'menuHeight',
    'menuWidth',
    'show_shadow',
    'sticky',
  ],
  logo: ['logo', 'show_logo', 'logoAlignment', 'logoWidth', 'cornerRadius'],
  elements: ['show_icon', 'show_label', 'show_hover', 'show_active_indicator'],
}
